import {ChangeDetectionStrategy, Component, effect, inject, linkedSignal, output, viewChild, WritableSignal} from '@angular/core';
import {AuthStore} from '@auth/data-access';
import {UserModel} from '@core/api/data-access';
import {UserStore} from '@data-users';
import {catchError, of, take} from 'rxjs';
import {UserFormModel} from '../../models/user-form.model';
import {UserFormComponent} from '../user-form/user-form.component';
import {ActiveUserStore} from '@active-user-management';

@Component({
	selector: 'dc-active-user-edit-profile',
	imports: [UserFormComponent],
	styles: `
		:host {
			@apply block w-full h-full;
		}
	`,
	template: `
		<dc-user-form
			[(user)]="user"
			[sections]="['GeneralDetails', 'ContactInfo', 'Password', 'ActiveUserPreferences', 'ActiveUserStamps']"
			formTitle="pages.users.edituser"
			formSubmitButtonText="dccomp.user-form.saveButton"
			formCancelButtonText="dccomp.user-form.cancelButton"
			(formSubmit)="onFormSubmit($event)"
			(formCancel)="onFormCancel()"
		/>
	`,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActiveUserEditProfileComponent {
	protected readonly userForm = viewChild(UserFormComponent);

	private readonly userStore = inject(UserStore);
	private readonly authStore = inject(AuthStore);
	private readonly activeUserStore = inject(ActiveUserStore);

	public readonly user: WritableSignal<UserModel> = linkedSignal(() => {
		const iUser = this.activeUserStore.user$();
		if (!iUser) return null;
		return UserModel.fromIUser(iUser);
	});

	public submitUser = output<UserModel | null>();
	public cancelForm = output<void>();

	constructor() {
		const userFormInitEffect = effect(async () => {
			const userForm = this.userForm();
			if (userForm) {
				if (userForm.userFormFactory.PasswordSection.setPasswordOptional()) {
					userFormInitEffect.destroy();
				}
			}
		});
	}

	public onFormSubmit(userFormModel: UserFormModel): void {
		this.userStore
			.updateUser(userFormModel.userModel)
			.pipe(
				catchError(error => {
					console.error(error);
					return of(null);
				}),
				take(1),
			)
			.subscribe(user => {
				if (user) {
					this.submitUser.emit(user);
					this.activeUserStore.updateActiveUserState(user, false);
				} else {
					this.submitUser.emit(null);
				}
			});
	}

	public onFormCancel(): void {
		this.cancelForm.emit();
	}
}
